import { Icon as BlueIcon } from '@blueprintjs/core';

import AvatarDefaultImg from '@assets/svg/avatar-placeholder.svg';
import { Image } from '@mantine/core';

import { useState } from '@hookstate/core';
import { getUserMembers } from '@services/queries/studios';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import {
  assignUsersToRequest,
  unassignUsersFromRequest,
} from '@services/queries/onboarding/assignUsersToRequest';
import { useRef } from 'react';
import { useMenuState } from '@szhsin/react-menu';
import GlobalOverlay from '@components/GlobalOverlay';
import {
  Avatar,
  Close,
  Divider,
  Header,
  InputContainer,
  InputSearch,
  Item,
  Items,
  Label,
  SearchIconContainer,
  Title,
  Remove,
  ItemNoFeedback,
  UsersSeparator,
  SearchCloseIconContainer,
  Wrapper,
  Menu,
  AssignToWrapper,
  EmptyAssignedTo,
  AssignedTo,
  AssineeImage,
  AsigneeName,
} from './RequestAssigneeDialog.styles';
import { RequestAssigneeDialogLoading } from './RequestAssigneeDialog.loading';

interface IChangeAssignee {
  threadId: string;
  selectedGroup: string;
  file: {
    avatar: string;
    name: string;
  };
}

export const RequestAssigneeDialog = ({
  threadId,
  selectedGroup,
  file,
}: IChangeAssignee) => {
  const searchLocal = useState<string>('');
  const searchUser = useState<string>('');

  const ref = useRef(null);

  const [menuProps, toggleMenu] = useMenuState({
    transition: false,
    unmountOnClose: true,
  });

  const isOpen = menuProps.state === 'open';

  const { data, isLoading, isError } = useQuery('members', getUserMembers);

  const findCurrentUser = data?.find(item => item.id === selectedGroup);

  const queryClient = useQueryClient();

  const assignRequest = useMutation(assignUsersToRequest, {
    onSuccess: () => {
      queryClient.invalidateQueries(['identificationRequests', 'assets']);
      toggleMenu(false);
    },
  });

  const unassignRequest = useMutation(unassignUsersFromRequest, {
    onSuccess: () => {
      queryClient.invalidateQueries(['identificationRequests', 'assets']);
      toggleMenu(false);
    },
  });

  const clearSearch = () => {
    searchUser.set('');
    searchLocal.set('');
  };
  return (
    <Wrapper onClick={e => e.stopPropagation()}>
      <AssignToWrapper
        ref={ref}
        onClick={() => {
          toggleMenu(true);
        }}
      >
        {file.avatar === null || !file.avatar ? (
          <EmptyAssignedTo>
            <BlueIcon color="#8F95B2" icon="small-plus" />
          </EmptyAssignedTo>
        ) : (
          <AssignedTo>
            <AssineeImage src={file.avatar} alt="A" />
            <AsigneeName>{file.name}</AsigneeName>
          </AssignedTo>
        )}

        {isOpen && <GlobalOverlay />}
      </AssignToWrapper>

      <Menu
        {...menuProps}
        align="center"
        anchorRef={ref}
        onClose={() => {
          toggleMenu(false);
          clearSearch();
        }}
      >
        <Header>
          <Title>Assign to</Title>
          <Close
            onClick={() => {
              toggleMenu(false);
              clearSearch();
            }}
          >
            <BlueIcon icon="cross" color="#8F95B2" />
          </Close>
        </Header>
        <Divider />

        <InputContainer>
          <SearchIconContainer>
            <BlueIcon icon="search" color="#8F95B2" size={12} />
          </SearchIconContainer>
          <InputSearch
            placeholder="User name or Email"
            value={searchLocal.get()}
            onChange={e => searchLocal.set(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                searchUser.set(searchLocal.get());
              }
            }}
          />
          {searchUser.get() && (
            <SearchCloseIconContainer
              onClick={() => {
                clearSearch();
              }}
            >
              <BlueIcon icon="small-cross" color="#8F95B2" />
            </SearchCloseIconContainer>
          )}
        </InputContainer>

        {isLoading && <RequestAssigneeDialogLoading />}

        {!isLoading && (
          <Items className="asset bscroll">
            {findCurrentUser && (
              <>
                <Label className="h1">Current Assignee</Label>
                <ItemNoFeedback className="asset">
                  <Item data-no-bg>
                    <Image
                      src={findCurrentUser.picture || AvatarDefaultImg}
                      alt=""
                      width="24px"
                      height="24px"
                      placeholder={
                        <Avatar
                          className="defaultImage"
                          src={AvatarDefaultImg}
                          alt=""
                        />
                      }
                    />

                    <Label>{findCurrentUser.name}</Label>
                  </Item>
                  <Remove
                    type="button"
                    onClick={() => {
                      unassignRequest.mutate({ threadId });
                    }}
                  >
                    <BlueIcon icon="cross" color="#D14343" size={12} />
                  </Remove>
                </ItemNoFeedback>
              </>
            )}

            {!isLoading &&
              !isError &&
              data &&
              (() => {
                const filteredUsers = data
                  .filter(item => item.id !== selectedGroup)
                  .filter(item =>
                    searchUser.get()
                      ? item.name
                          ?.toLowerCase()
                          .includes(searchUser.get().toLowerCase()) ||
                        item.email
                          ?.toLowerCase()
                          .includes(searchUser.get().toLowerCase())
                      : true,
                  );

                return (
                  <>
                    {(findCurrentUser || !filteredUsers) && <UsersSeparator />}

                    {filteredUsers.map(item => (
                      <Item
                        key={item.id}
                        onClick={() => {
                          assignRequest.mutate({
                            user: {
                              name: item.name,
                              avatar: item.picture,
                              email: item.email,
                              id: item.id,
                              authorId: item.id,
                            },
                            threadId,
                          });
                        }}
                      >
                        <Image
                          src={item.picture || AvatarDefaultImg}
                          alt=""
                          width="24px"
                          height="24px"
                          placeholder={
                            <Avatar
                              className="defaultImage"
                              src={AvatarDefaultImg}
                              alt=""
                            />
                          }
                        />

                        <Label>{item.name}</Label>
                      </Item>
                    ))}
                  </>
                );
              })()}
          </Items>
        )}
      </Menu>
    </Wrapper>
  );
};
